import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { VEHICLE_ICON_PATH, WAY_POINT_ICON_PATH } from '../utils/icons';
import styles from '../TripTrackingV2.module.scss';

const HamburgerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const RouteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M4 20c4-8 4-8 8-8s4-8 8-8" />
  </svg>
);

const TruckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="12" height="10" rx="1" />
    <path d="M14 10h4l3 3v3h-7z" />
    <circle cx="6.5" cy="18" r="1.6" />
    <circle cx="17" cy="18" r="1.6" />
  </svg>
);

const PingIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="4" />
  </svg>
);

const WaypointIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s7-7.75 7-13a7 7 0 0 0-14 0c0 5.25 7 13 7 13z" />
  </svg>
);

// Plain <input type="checkbox"> has no `indeterminate` prop - it can only be
// set imperatively on the DOM node - so this wraps that in a ref.
const LayerCheckbox = ({ className, checked, indeterminate, bold, onChange, children }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <label className={classNames(className, { [styles.layerRowBold]: bold })}>
      <input type="checkbox" ref={inputRef} checked={checked} onChange={onChange} />
      <span>{children}</span>
    </label>
  );
};

const LegendSwatch = ({ color, isLine }) => (
  <span className={isLine ? styles.legendSwatchLine : styles.legendSwatchDot} style={{ background: color }} />
);

// The actual vehicle marker glyph, filled with the status color - so this
// legend shows what's really on the map instead of a generic dot.
const TruckSwatch = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" className={styles.legendSwatchIcon}>
    <path d={VEHICLE_ICON_PATH} fill={color} />
  </svg>
);

// The actual S/D/waypoint pin glyph, filled with its legend color.
const WayPointSwatch = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" className={styles.legendSwatchIcon}>
    <path d={WAY_POINT_ICON_PATH} fill={color} stroke="#ffffff" strokeWidth="0.5" />
  </svg>
);

const GROUP_ICONS = { Routes: RouteIcon, Waypoints: WaypointIcon, 'Vehicle Status': TruckIcon, 'Ping Status': PingIcon };
const GROUP_SWATCHES = { 'Vehicle Status': TruckSwatch, Waypoints: WayPointSwatch };

// Read-only color key (no checkboxes) - one card per legend category
// (routes/vehicle status/ping status), each item optionally carrying its
// own sub-status rows (truck_legend/ping_legend sub_types). Vehicle Status
// and Waypoints swatches use the real marker glyphs instead of a plain dot.
const MapKeyGroup = ({ group }) => {
  const Icon = GROUP_ICONS[group.title];
  const Swatch = GROUP_SWATCHES[group.title] || LegendSwatch;
  return (
    <div className={styles.legendCard}>
      <div className={styles.legendCardTitle}>
        {Icon && <Icon />}
        {group.title}
      </div>
      {group.items.map((item) => (
        <div key={item.label}>
          <div className={styles.legendRow}>
            <Swatch color={item.color} isLine={item.isLine} />
            <span>{item.label}</span>
          </div>
          {(item.subItems || []).map((sub) => (
            <div className={styles.legendSubRow} key={sub.label}>
              <Swatch color={sub.color} />
              <span>{sub.label}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// Config-driven so new toggles (tollgates, etc.) are a one-line addition
// once that list is decided - no changes needed here.
const MapLayersDrawer = ({ layers, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles.layersDrawer}>
      <button
        type="button"
        className={classNames(styles.layersToggleButton, { [styles.layersToggleButtonActive]: isOpen })}
        onClick={() => setIsOpen((open) => !open)}
      >
        <HamburgerIcon />
      </button>
      {isOpen && (
        <div className={styles.layersPanel}>
          <div className={styles.layersPanelHeader}>
            <span>Map Layers</span>
            <button type="button" className={styles.layersCloseButton} onClick={() => setIsOpen(false)} aria-label="Close">
              &times;
            </button>
          </div>
          <div className={styles.layersPanelBody}>
            {layers.map((layer) => {
              if (layer.legend) {
                return (
                  <div key={layer.key}>
                    {layer.groups.map((group) => <MapKeyGroup group={group} key={group.title} />)}
                  </div>
                );
              }
              if (layer.children) {
                return (
                  <div className={styles.filterCard} key={layer.key}>
                    <div className={styles.filterCardTitle}>{layer.label}</div>
                    {layer.children.map((child) => (
                      <LayerCheckbox
                        className={styles.layerChildRow}
                        key={child.key}
                        checked={child.checked}
                        indeterminate={child.indeterminate}
                        bold={child.isAll}
                        onChange={() => onToggle(child.key)}
                      >
                        {child.label}
                      </LayerCheckbox>
                    ))}
                  </div>
                );
              }
              return (
                <div className={styles.filterCard} key={layer.key}>
                  <LayerCheckbox className={styles.layerRow} checked={layer.checked} onChange={() => onToggle(layer.key)}>
                    {layer.label}
                  </LayerCheckbox>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapLayersDrawer;
